import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, StyleSheet, ViewStyle } from 'react-native';

import { PlayerInfoFragment, PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import Arrow from '@/assets/icons/arrow';
import { AnimatedNumber } from '@/components/AnimatedNumber';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DesignSystemColor } from '@/components/lib/theme';
import { GroupProjectionsIcons } from '@/feature/lobby/components/GroupProjectionsIcons';
import { SingleProjectionIcon } from '@/feature/lobby/components/SingleProjectionIcon';
import {
    checkIfPromoProjection,
    checkIfRegularProjection,
    checkIfSpecialOrFreePickProjection,
} from '@/feature/lobby/utils/lineupsUtils';
import { useShouldShowCurrentValue } from '@/hooks/use-should-show-current-value';
import { common, designSystem } from '@/styles/styles';
import { EventAttributesType, League, Outcome, Projection, ProjectionType, Result } from '@/types/api.generated';
import { AtLeast } from '@/types/utils';
import { findEventAttribute } from '@/utils/fantasy-attribute-utils';
import { formatPlayerName } from '@/utils/format-player-name';
import { formatPlayerPositionAndTeam, getPlayerPositionOrRank } from '@/utils/formatPlayerInfo';
import { gameUtils } from '@/utils/games';
import { getDisplayedProjectionValue } from '@/utils/projections';

import { useLiveGamePeriod } from '../hooks/use-live-game-period';

type Props = {
    player: PlayerInfoFragment;
    projections?: PlayerProjectionFragment[];
    teamName: string;
    selectedProjection?: PlayerProjectionFragment;
    newProjection?: PlayerProjectionFragment;
    event?: EventInfo;
    isVoided?: boolean;
    testID?: string;
    isSuspended?: boolean;
    hideGameTime?: boolean;
    outcome?: Outcome;
};

export const PlayerDetailsTile = memo(
    ({ player, selectedProjection, event, isVoided, testID, isSuspended, hideGameTime }: Props) => {
        const { t } = useTranslation('betslip_pickem');
        const formattedName = formatPlayerName(player, false);
        const showCurrent = useShouldShowCurrentValue(selectedProjection);
        const playerPosition = getPlayerPositionOrRank(player, event);

        return (
            <Box>
                <Row gap={'s4'} justifyContent={'center'} alignSelf={'stretch'}>
                    <Text
                        variant={'titleMedium'}
                        color={ds.colorToken.text.primary}
                        numberOfLines={1}
                        style={styles.formattedName}
                    >
                        {formattedName}
                    </Text>
                    <Text variant={'bodyMedium'} color={'neutral300'}>
                        {playerPosition}
                    </Text>
                </Row>
                <Box style={styles.eventTile}>
                    <EventDetails
                        event={event}
                        isVoided={isVoided}
                        player={player}
                        testID={testID}
                        isSuspended={isSuspended}
                        viewMode={'tile'}
                        hideGameTime={hideGameTime}
                    />
                </Box>
                {showCurrent ? (
                    <Text
                        variant="labelMedium"
                        fontWeight={400}
                        marginTop={'s3'}
                        textAlign={'center'}
                        color={gameUtils.getEventInfoTextColor(isVoided, false, undefined, isSuspended, 'tile')}
                    >
                        {t('current')}:{' '}
                        <Text variant="labelMedium" fontWeight={600} color={ds.colorToken.text.primary}>
                            {selectedProjection?.currentValue || 0}
                        </Text>
                    </Text>
                ) : null}
            </Box>
        );
    }
);

export const PlayerDetails = ({
    projections,
    player,
    teamName,
    selectedProjection,
    newProjection,
    event,
    isVoided,
    testID,
    isSuspended,
    hideGameTime,
    outcome,
}: Props) => {
    const showProjectionDetails = isVoided && selectedProjection;
    const hasNonRegularProjections = projections?.some(projection => projection.type !== ProjectionType.Regular);
    const showCurrent = useShouldShowCurrentValue(selectedProjection);

    const isGroupProjectionDisplayed = !selectedProjection && hasNonRegularProjections;
    return (
        <Box>
            <PlayerDescription
                player={player}
                teamName={teamName}
                isVoided={isVoided}
                eventLeague={event?.league}
                isSuspended={isSuspended}
            />
            {isSuspended ? (
                <Text
                    color={'neutral400'}
                    variant={'bodyMedium'}
                    lineHeight={20}
                    style={styles.selectedProjectionContentPadding}
                >
                    Suspended
                </Text>
            ) : showProjectionDetails || projections?.length ? (
                <PlayerProjectionDetails
                    selectedProjection={selectedProjection}
                    projections={projections}
                    newProjection={newProjection}
                    isVoided={isVoided}
                    outcome={outcome}
                    triggerEducationalSheet
                />
            ) : null}
            {showCurrent && selectedProjection ? (
                <CurrentValueDetails
                    selectedProjection={selectedProjection}
                    isSuspended={isSuspended}
                    isVoided={isVoided}
                />
            ) : null}
            <EventDetails
                event={event}
                isVoided={isVoided}
                player={player}
                testID={testID}
                isSuspended={isSuspended}
                hideGameTime={hideGameTime}
                isGroupProjectionDisplayed={isGroupProjectionDisplayed}
            />
        </Box>
    );
};

type CurrentValueDetailsProps = {
    selectedProjection: PlayerProjectionFragment;
    isSuspended?: boolean;
    isVoided?: boolean;
};

export const CurrentValueDetails = ({ selectedProjection, isSuspended, isVoided }: CurrentValueDetailsProps) => {
    const { t } = useTranslation('betslip_pickem');

    return (
        <Text
            color={gameUtils.getEventInfoTextColor(isVoided, false, undefined, isSuspended)}
            textDecorationLine={isVoided ? 'line-through' : 'none'}
            variant="bodySmall"
            style={styles.eventPaddingTop}
        >
            {t('current')}: {selectedProjection.currentValue}
        </Text>
    );
};

// Display player name, position and team name in a row
type PlayerDescriptionProps = {
    player: PlayerInfoFragment;
    teamName?: string;
    isVoided?: boolean;
    eventLeague?: League;
    showFullName?: boolean;
    isSuspended?: boolean;
};
export const PlayerDescription = memo(
    ({ player, teamName, isVoided, eventLeague, showFullName, isSuspended }: PlayerDescriptionProps) => {
        const formattedName = formatPlayerName(player, showFullName);
        const formattedPositionAndTeam = useMemo(
            () => formatPlayerPositionAndTeam(player, teamName, eventLeague),
            [eventLeague, player, teamName]
        );

        const textColor = getColor(isVoided, isSuspended, ds.colorToken.text.primary);
        const playerPositionColor = getColor(isVoided, isSuspended, 'neutral300');

        return (
            <Text style={styles.playerInfo} numberOfLines={1} color={textColor}>
                <Text variant="titleMedium" lineHeight={20} color={textColor}>
                    {formattedName}
                </Text>
                <Text style={styles.position} letterSpacing={-0.08} variant="bodySmall" color={playerPositionColor}>
                    {formattedPositionAndTeam}
                </Text>
            </Text>
        );
    }
);

const getColor = (isVoided: boolean | undefined, isSuspended: boolean | undefined, textColor: DesignSystemColor) => {
    if (isVoided || isSuspended) {
        return 'neutral400';
    }

    return textColor;
};

// Display all informations about projections (icons + details)
type PlayerProjectionDetailsProps = {
    selectedProjection?: PlayerProjectionFragment;
    projections?: PlayerProjectionFragment[];
    newProjection?: PlayerProjectionFragment;
    isVoided?: boolean;
    outcome?: Outcome;
    triggerEducationalSheet?: boolean;
    hideCrossedOutValue?: boolean;
};

export const PlayerProjectionDetails = memo(
    ({
        selectedProjection,
        projections,
        newProjection,
        isVoided,
        outcome,
        triggerEducationalSheet = false,
        hideCrossedOutValue,
    }: PlayerProjectionDetailsProps) => {
        const hasPromoProjections = projections?.some(projection => checkIfPromoProjection(projection.type));
        const arrowStyle = outcome ? (outcome === Outcome.More ? {} : common.rotate180) : {};

        const showArrow = useMemo(() => {
            if (!selectedProjection) {
                return false;
            }

            if (selectedProjection.allowedOptions && selectedProjection.allowedOptions.length > 1 && !outcome) {
                // don't show the arrow if ther's a single projection with multiple outcomes and no outcome selected
                return false;
            }

            return true;
        }, [outcome, selectedProjection]);

        return (
            <Row
                style={
                    selectedProjection || !hasPromoProjections
                        ? styles.selectedProjectionContentPadding
                        : styles.projectionsContentAlignment
                }
                alignItems={'center'}
            >
                {/** If showing as projection changed, show the new projection icons */}
                <ProjectionIcons
                    selectedProjection={newProjection ?? selectedProjection}
                    projections={projections}
                    triggerEducationalSheet={triggerEducationalSheet}
                />
                <Row flex={1} alignItems={'center'}>
                    {showArrow ? (
                        <>
                            <Arrow
                                color={isVoided ? designSystem.colors.neutral400 : ds.colorValue.icon.primary}
                                style={arrowStyle}
                                testID="outcome-arrow"
                            />
                            <SizedBox value={4} />
                        </>
                    ) : null}
                    <ProjectionDescription
                        selectedProjection={selectedProjection}
                        projections={projections}
                        newProjection={newProjection}
                        isVoided={isVoided}
                        hideCrossedOutValue={hideCrossedOutValue}
                    />
                </Row>
            </Row>
        );
    }
);

export const SelectedProjectionDescription = ({
    style,
    shouldShowCrossedOutValue,
    crossedOutValue,
    projectionValue,
    projectionDescription,
    color,
}: {
    style?: StyleProp<ViewStyle>;
    shouldShowCrossedOutValue?: boolean;
    crossedOutValue?: number;
    projectionValue: number;
    projectionDescription: string;
    color?: DesignSystemColor;
}) => {
    return (
        <Row style={style}>
            {shouldShowCrossedOutValue ? (
                <>
                    <Text
                        textDecorationLine={'line-through'}
                        color={'neutral300'}
                        variant={'bodyMedium'}
                        lineHeight={20}
                    >
                        {crossedOutValue?.toFixed(1)}
                    </Text>
                    <SizedBox value={4} />
                </>
            ) : null}
            <AnimatedNumber
                decimals={1}
                value={projectionValue}
                color={color ?? ds.colorToken.text.primary}
                variant={'bodyMedium'}
                letterSpacing={-0.23}
                alignText={'flex-start'}
                lineHeight={20}
            />
            <SizedBox value={4} />
            <Text
                variant={'bodyMedium'}
                color={color ?? ds.colorToken.text.primary}
                letterSpacing={-0.23}
                lineHeight={20}
                numberOfLines={1}
            >
                {projectionDescription}
            </Text>
        </Row>
    );
};

// Display the projection details for selected projection or all projections and new projection if selected projection changed
type ProjectionDescriptionProp = {
    selectedProjection?: PlayerProjectionFragment;
    projections?: PlayerProjectionFragment[];
    newProjection?: PlayerProjectionFragment;
    isVoided?: boolean;
    viewMode?: 'row' | 'tile';
    isSuspended?: boolean;
    handleToggleProjection?: (goNext: boolean) => void;
    hasNextProjection?: boolean;
    hasPreviousProjection?: boolean;
    hideCrossedOutValue?: boolean;
};

export const ProjectionDescription = ({
    selectedProjection,
    projections,
    newProjection,
    isVoided,
    hideCrossedOutValue = false,
}: ProjectionDescriptionProp) => {
    const isSelectedProjectionSpecialOrFree = checkIfSpecialOrFreePickProjection(selectedProjection?.type);
    const isNewProjectionRegular = checkIfRegularProjection(newProjection?.type);

    const selectedProjectionValue = getDisplayedProjectionValue(selectedProjection);
    const newProjectionValue = getDisplayedProjectionValue(newProjection);
    const hasSameValue = selectedProjectionValue === newProjectionValue;
    const isOnlyTypeChangedToRegular = isNewProjectionRegular && hasSameValue;

    //Crossed out value
    // - when special/free pick -> the regular selected value
    // - when new projection received -> previous selected projection value
    let crossedOutValue;
    if (newProjection) {
        crossedOutValue = selectedProjectionValue;
    } else if (isSelectedProjectionSpecialOrFree) {
        crossedOutValue = selectedProjection?.value;
    }
    const hasCrossedOutValue = (crossedOutValue ?? 0) > 0;

    const shouldShowCrossedOutValue = !hideCrossedOutValue && hasCrossedOutValue && !isOnlyTypeChangedToRegular;

    const { t } = useTranslation('betslip_pickem');

    return selectedProjection ? (
        // Using nested <Text> components to handle wrapping when the projection text is long and all buttons need to be displayed.
        isVoided ? (
            <Text>
                {shouldShowCrossedOutValue ? (
                    <>
                        <Text color={'neutral400'} variant={'bodyMedium'} lineHeight={20}>
                            {crossedOutValue}
                        </Text>
                        <SizedBox value={4} />
                    </>
                ) : null}
                <Text variant={'bodyMedium'} color={'neutral400'} lineHeight={20}>
                    {`${newProjection ? newProjectionValue : selectedProjectionValue} ${selectedProjection.label}`}
                </Text>
            </Text>
        ) : (
            <SelectedProjectionDescription
                style={styles.selectedProjectionContainer}
                crossedOutValue={crossedOutValue}
                projectionValue={(newProjection ? newProjectionValue : selectedProjectionValue) ?? 0}
                shouldShowCrossedOutValue={shouldShowCrossedOutValue}
                projectionDescription={selectedProjection.label}
            />
        )
    ) : (
        <Row alignItems={'center'} flexWrap={'wrap'}>
            <Text style={styles.position} variant="bodyMedium" numberOfLines={1} ellipsizeMode="tail" lineHeight={20}>
                {`${t('projection', { count: projections?.length })}`}
            </Text>
        </Row>
    );
};

// Display the projection icons for selected projection or all projections
type ProjectionIconsProp = {
    selectedProjection?: AtLeast<Projection, 'type'>;
    projections?: PlayerProjectionFragment[];
    triggerEducationalSheet?: boolean;
};

export const ProjectionIcons = ({ selectedProjection, projections, triggerEducationalSheet }: ProjectionIconsProp) => {
    const nonRegularSelected = !checkIfRegularProjection(selectedProjection?.type);
    return selectedProjection ? (
        <>
            <SingleProjectionIcon
                selectedProjection={selectedProjection}
                triggerEducationalSheet={triggerEducationalSheet}
            />
            {nonRegularSelected ? <SizedBox value={6} /> : null}
        </>
    ) : (
        <>
            <GroupProjectionsIcons projections={projections} triggerEducationalSheet={triggerEducationalSheet} />
        </>
    );
};

type EventRoundIndicatorProps = {
    roundIndicator?: string;
    isVoided?: boolean;
};

const EventRoundIndicator = ({ roundIndicator, isVoided }: EventRoundIndicatorProps) =>
    roundIndicator ? (
        <Text variant="bodySmall" color={gameUtils.getEventInfoTextColor(isVoided, false, ds.colorToken.text.primary)}>
            R{roundIndicator}
            <Text variant="bodySmall" color={gameUtils.getEventInfoTextColor(isVoided)}>
                {' • '}
            </Text>
        </Text>
    ) : null;

type EventDescriptionProps = {
    event: EventInfo;
    isVoided?: boolean;
    player: PlayerInfoFragment;
    testID?: string;
    projectionStatus?: {
        result?: Result;
        currentValue?: number;
    };
    isSuspended?: boolean;
    viewMode?: 'row' | 'tile';
    hideGameTime?: boolean;
    isGroupProjectionDisplayed?: boolean;
};

const EventDescription = ({
    event,
    isVoided,
    player,
    testID,
    projectionStatus,
    isSuspended,
    viewMode = 'row',
    hideGameTime,
    isGroupProjectionDisplayed,
}: EventDescriptionProps) => {
    const { versusText, eventTime, tournamentName } = gameUtils.createEventDescriptionLabel({
        projectionStatus,
        event,
        player,
    });
    const isLive = gameUtils.isLive(event);
    const isStartingSoon = eventTime === 'Starting soon';
    const roundIndicator = findEventAttribute(event, EventAttributesType.RoundNumber);
    const shouldHighlightLiveEvent = isLive && !projectionStatus;
    const isRow = viewMode === 'row';
    const eventDescriptionVariant = isRow ? (shouldHighlightLiveEvent ? 'titleSmall' : 'bodySmall') : 'bodySmaller';

    const livePeriodLabel = useLiveGamePeriod(event);
    const displayedTime = isLive && livePeriodLabel && !hideGameTime ? livePeriodLabel : eventTime;

    return (
        <Text
            numberOfLines={1}
            style={isRow ? (isGroupProjectionDisplayed ? styles.smallEventPaddingTop : styles.eventPaddingTop) : {}}
            testID={testID}
            color={gameUtils.getEventInfoTextColor(isVoided, false, undefined, isSuspended, viewMode)}
        >
            <EventRoundIndicator roundIndicator={roundIndicator} isVoided={isVoided} />
            <Text
                color={gameUtils.getEventInfoTextColor(isVoided, false, undefined, isSuspended, viewMode)}
                variant={isRow ? 'bodySmall' : 'bodySmaller'}
                lineHeight={isRow ? 20 : 16}
                style={styles.playerInfo}
                numberOfLines={1}
            >
                {`${versusText}${eventTime && versusText ? ' • ' : ''}`}
            </Text>
            <Text
                color={gameUtils.getEventInfoTextColor(
                    isVoided,
                    shouldHighlightLiveEvent,
                    undefined,
                    isSuspended,
                    viewMode,
                    isStartingSoon
                )}
                variant={eventDescriptionVariant}
                lineHeight={isRow ? 20 : 16}
                fontWeight={shouldHighlightLiveEvent ? 600 : 400}
                style={styles.playerInfo}
                numberOfLines={1}
                testID={`displayed-time-for-${testID || ''}-${displayedTime}`}
            >
                {displayedTime}
            </Text>
            <Text
                color={gameUtils.getEventInfoTextColor(isVoided, false, undefined, isSuspended, viewMode)}
                variant={isRow ? 'bodySmall' : 'bodySmaller'}
                lineHeight={isRow ? 20 : 16}
                style={styles.playerInfo}
                numberOfLines={1}
            >
                {tournamentName ? ` • ${tournamentName}` : ''}
            </Text>
        </Text>
    );
};

// Display the event details in a row
type EventDetailsProps = {
    event?: EventInfo;
    isVoided?: boolean;
    player: PlayerInfoFragment;
    testID?: string;
    projectionStatus?: {
        result?: Result;
        currentValue?: number;
    };
    isSuspended?: boolean;
    viewMode?: 'row' | 'tile';
    hideGameTime?: boolean;
    isGroupProjectionDisplayed?: boolean;
};

export const EventDetails = memo(
    ({
        event,
        isVoided,
        player,
        testID,
        projectionStatus,
        isSuspended,
        viewMode = 'row',
        hideGameTime,
        isGroupProjectionDisplayed,
    }: EventDetailsProps) => {
        return !event ? (
            <Text
                style={viewMode === 'row' ? styles.eventPaddingTop : {}}
                testID={testID}
                color={gameUtils.getEventInfoTextColor(isVoided)}
                variant={viewMode === 'row' ? 'bodySmall' : 'labelMedium'}
            >
                -
            </Text>
        ) : (
            <EventDescription
                event={event}
                player={player}
                projectionStatus={projectionStatus}
                isVoided={isVoided}
                testID={testID}
                isSuspended={isSuspended}
                viewMode={viewMode}
                hideGameTime={hideGameTime}
                isGroupProjectionDisplayed={isGroupProjectionDisplayed}
            />
        );
    }
);

const styles = StyleSheet.create({
    eventTile: {
        width: '100%',
        alignItems: 'center',
    },
    position: {
        flexShrink: 1,
    },
    formattedName: {
        flexShrink: 1,
    },
    playerInfo: {
        flexWrap: 'wrap',
    },
    projectionsContentAlignment: {
        paddingTop: 2.5,
        marginLeft: -2,
    },
    selectedProjectionContentPadding: {
        paddingTop: 4.5,
    },
    eventPaddingTop: {
        paddingTop: 3.5,
    },
    smallEventPaddingTop: {
        paddingTop: 1.5,
    },
    selectedProjectionContainer: {
        flex: 1,
        flexWrap: 'wrap',
    },
});
